import { APIScope, LocationScope } from "@swivl-mono/types";
import {
  DropdownContainer,
  DropdownOption,
  DropdownPosition,
  HeaderTabsComponent,
  LayoutWithWideSidebar,
  SearchInput,
  Switch,
  SwitchVariant
} from "@swivl-mono/ui";
import React, { useCallback, useEffect } from "react";
import { BiDotsVerticalRounded } from "react-icons/bi";
import { Outlet, useNavigate } from "react-router-dom";

import { useHasPermission } from "../../Helpers/Permissions/useHasPermission";
import { usePropertiesGetLocationIds } from "../../Hooks/usePropertiesGetLocationIds";
import { useViewCustomizations } from "../../Hooks/useViewCustomizations";
import LocationsModel from "../../Models/Locations/Locations.Model";
import { Member } from "../../Models/Member/Member.Current";
import ModalModel from "../../Models/Modal/Modal.Model";
import { NavBar } from "../../Models/NavBar/NavBar.Model";
import "../../Worksheets/Worksheets.scss";
import { LocationsListComponent } from "./Components/Locations.List.Component";
import { AddLocationOrGroupModal } from "./Modals/AddLocationsOrGroup.Modal";

export enum PropertiesViewPage {
  DETAILS = "details",
  DETAILS_V3 = "details-v3",
  CORTEX = "cortex",
  POLICY_WORKSHEETS = "policy-worksheets",
  BRANDS = "brands",
  CHATBOTS = "chatbots",
  SPOT = "spot",
  SEQUENCES = "sequences",
  REPORTS = "reports"
}

interface PropertiesNavContentProps {
  items: { to: string; label: string }[];
}

function PropertiesNavContent(props: PropertiesNavContentProps): React.ReactNode {
  return (
    <div className="flex items-center gap-3">
      <span className="mr-8 font-poppins text-lg font-semibold">Property Manager</span>
      <HeaderTabsComponent items={props.items} />
    </div>
  );
}

interface SidebarHeaderProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  onAddLocation: () => void;
  onAddGroup: () => void;
}

function SidebarHeader(props: SidebarHeaderProps): React.ReactNode {
  const { searchQuery, onSearchChange, onAddLocation, onAddGroup } = props;
  const [showMenu, setShowMenu] = React.useState(false);
  const [viewCustomizations, toggleViewCustomizations] = useViewCustomizations();
  const canCreate = useHasPermission(APIScope.LOCATION_CREATE);

  const handleClear = (): void => {
    onSearchChange("");
  };

  const handleSelect = (value: string): void => {
    if (value === "add-location") {
      onAddLocation();
    } else if (value === "add-group") {
      onAddGroup();
    }
  };

  return (
    <div className="flex w-full items-center gap-2 border-b border-grey-200 p-3">
      <SearchInput placeholder="Search..." value={searchQuery} onChange={(e) => onSearchChange(e.target.value)} onClear={handleClear} />
      <DropdownContainer
        isOpen={showMenu}
        onOpenChange={setShowMenu}
        position={DropdownPosition.RIGHT}
        trigger={
          <button className="flex cursor-pointer items-center justify-center text-grey-600 hover:text-grey-800" aria-label="More options">
            <BiDotsVerticalRounded size={18} />
          </button>
        }
      >
        <div className="flex w-full items-center justify-between gap-3 p-2">
          <span className="body-sm">Customization Count</span>
          <Switch on={viewCustomizations} onSet={toggleViewCustomizations} variant={SwitchVariant.TOGGLE} />
        </div>
        {canCreate && <div className="my-1 h-px w-full bg-grey-200" />}
        {canCreate && <DropdownOption option={{ label: "Add Location", value: "add-location" }} onSelect={handleSelect} />}
        {canCreate && <DropdownOption option={{ label: "Add Group", value: "add-group" }} onSelect={handleSelect} />}
      </DropdownContainer>
    </div>
  );
}

export function PropertiesView() {
  const [searchQuery, setSearchQuery] = React.useState("");

  const page = usePropertiesGetLocationIds();
  const navigate = useNavigate();
  const canViewLocationDetails = useHasPermission(APIScope.LOCATION_DETAILS_READ);

  useEffect(() => {
    LocationsModel.getGroupsAndLocations(false);
  }, []);

  const handleAddLocation = useCallback((): void => {
    ModalModel.showModalWithContent(<h2>Add Location</h2>, <AddLocationOrGroupModal type="location" navigate={navigate} />);
  }, [navigate]);

  const handleAddGroup = useCallback((): void => {
    ModalModel.showModalWithContent(<h2>Add Group</h2>, <AddLocationOrGroupModal type="group" navigate={navigate} />);
  }, [navigate]);

  // Note: Details v3 + Policies live in their own top-level surface at
  // `/properties-v3` (see PropertiesV3.View.tsx). They are intentionally not
  // duplicated as tabs here.
  const detailsTabs = canViewLocationDetails
    ? [
        { to: `${page.preamble}/page/${PropertiesViewPage.DETAILS}`, label: "Details" },
        { to: `${page.preamble}/page/${PropertiesViewPage.CORTEX}`, label: "Cortex" }
      ]
    : [];

  let headerTabItems = [
    ...detailsTabs,
    { to: `${page.preamble}/page/${PropertiesViewPage.SPOT}`, label: "spot.is" },
    { to: `${page.preamble}/page/${PropertiesViewPage.REPORTS}`, label: "Reports" }
  ];

  if (page.type === LocationScope.ORG) {
    if (Member.canSeeOrgSettings) {
      headerTabItems = [
        ...detailsTabs,
        { to: `${page.preamble}/page/${PropertiesViewPage.SPOT}`, label: "spot.is" },
        { to: `${page.preamble}/page/${PropertiesViewPage.REPORTS}`, label: "Reports" },
        { to: `${page.preamble}/page/${PropertiesViewPage.BRANDS}`, label: "Brands" },
        { to: `${page.preamble}/page/${PropertiesViewPage.CHATBOTS}`, label: "Chatbot" }
      ];
    } else {
      headerTabItems = [];
    }
  }

  useEffect(() => {
    NavBar.set(<PropertiesNavContent items={headerTabItems} />, null, null, "Property Manager");
  }, [page.preamble, page.type, handleAddLocation, handleAddGroup, canViewLocationDetails]);

  return (
    <LayoutWithWideSidebar
      sidebarClassName="!overflow-visible"
      sidebar={
        <div className="flex h-full flex-col">
          <div className="sticky top-0 z-60 w-full overflow-visible bg-white">
            <SidebarHeader searchQuery={searchQuery} onSearchChange={setSearchQuery} onAddLocation={handleAddLocation} onAddGroup={handleAddGroup} />
          </div>
          <div className="flex-1 overflow-y-auto pb-[40px]">
            <LocationsListComponent searchQuery={searchQuery} {...page} />
          </div>
        </div>
      }
      contentNoPadding={true}
      content={
        <div className="LocationView__content relative h-full">
          <Outlet />
        </div>
      }
    />
  );
}
